import { toast } from 'react-toastify';
import ApiBasicErrorResponse from '../shared/interfaces/api-basic-error-response.interface';
import ApiErrorResponse from '../shared/interfaces/api-error-response.interface';
import ApiValidateErrorResponse from '../shared/interfaces/api-validate-error-response.interface';

function isApiErrorResponse(data: unknown): data is ApiErrorResponse {
    return (
        typeof data === 'object' &&
        data !== null &&
        'timestamp' in data &&
        'statusCode' in data &&
        'error' in data &&
        'message' in data &&
        'path' in data
    );
}

function isApiValidateErrorResponse(
    data: any,
): data is ApiValidateErrorResponse {
    const firstError = data?.errors?.[0];

    return (
        typeof data === 'object' &&
        data !== null &&
        'errors' in data &&
        Array.isArray(data.errors) &&
        data.errors.length > 0 &&
        typeof firstError === 'object' &&
        firstError !== null &&
        'property' in firstError &&
        ('constraints' in firstError || 'children' in firstError)
    );
}

function flattenValidationMessages(
    errors: ApiValidateErrorResponse['errors'],
): string[] {
    return errors.flatMap((err) => {
        const ownMessages = err.constraints ? Object.values(err.constraints) : [];
        const childMessages = err.children?.length
            ? flattenValidationMessages(err.children)
            : [];

        return [...ownMessages, ...childMessages];
    });
}

function isApiBasicErrorResponse(data: unknown): data is ApiBasicErrorResponse {
    return (
        typeof data === 'object' &&
        data !== null &&
        'statusCode' in data &&
        'message' in data
    );
}

function getResponseErrorMessage(
    error: unknown,
    defaultMessage: string,
): string | string[] {
    if (isApiValidateErrorResponse(error)) {
        const messages = flattenValidationMessages(error.errors);
        return messages.length > 0 ? messages : defaultMessage;
    }

    if (isApiErrorResponse(error)) {
        return typeof error.message === 'string' || Array.isArray(error.message)
            ? error.message
            : defaultMessage;
    }

    if (isApiBasicErrorResponse(error)) {
        return error.message;
    }

    if (error instanceof Error && error.message) {
        return error.message;
    }

    return defaultMessage;
}

export default function handleResponseError(
    error: unknown,
    defaultMessage: string,
) {
    const errorMessage = getResponseErrorMessage(error, defaultMessage);

    if (Array.isArray(errorMessage)) {
        const tenSeconds = 10000;

        errorMessage.forEach((message, messagesDisplayed) =>
            toast.error(message, {
                autoClose: (messagesDisplayed + 1) * tenSeconds,
            }),
        );

        return;
    }

    toast.error(errorMessage);
}
